/**
 * Remove a contiguous slice of sibling nodes from `parent` using the Range API,
 * which lets the browser batch the removal into a single layout pass.
 *
 * Falls back to individual `removeChild` calls when:
 * - Only one node is supplied (Range setup would be more work than the removal).
 * - The Range API is unavailable (SSR / non-browser environment).
 * - The supplied nodes are not contiguous siblings.
 *
 * @param parent  Common parent element.
 * @param nodes   Nodes to remove. Nodes whose `parentNode` is not `parent` are
 *                skipped silently.
 */
export function batchRemoveRange(parent: Node, nodes: Node[]): void {
    const len = nodes.length
    if (len === 0) return

    // Single-node fast path.
    if (len === 1) {
        const n = nodes[0]
        if (n.parentNode === parent) parent.removeChild(n)
        return
    }

    // Range API availability — primarily for SSR / Node environments without
    // a full DOM, but we still need to remove individually to honour the
    // public contract.
    if (typeof document === 'undefined' || typeof document.createRange !== 'function') {
        for (let i = 0; i < len; i++) {
            const n = nodes[i]
            if (n.parentNode === parent) parent.removeChild(n)
        }
        return
    }

    // Range works correctly only for nodes that are (a) direct children of
    // `parent` AND (b) consecutive siblings. Both must hold.
    let contiguous = true
    for (let i = 1; i < len; i++) {
        if (nodes[i - 1].nextSibling !== nodes[i]) {
            contiguous = false
            break
        }
    }

    if (contiguous && nodes[0].parentNode === parent) {
        const range = document.createRange()
        range.setStartBefore(nodes[0])
        range.setEndAfter(nodes[len - 1])
        range.deleteContents()
        return
    }

    // Non-contiguous or attached to a different parent — fall back to
    // individual removal, skipping nodes that aren't actually our children.
    for (let i = 0; i < len; i++) {
        const n = nodes[i]
        if (n.parentNode === parent) parent.removeChild(n)
    }
}
